import { useEffect, useRef, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { WifiOff } from "lucide-react";

const SHOW_DEBOUNCE_MS = 1500;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Native-only full-screen offline overlay.
 * Renders nothing and registers no listeners on web.
 */
export default function NativeOfflineGate() {
  const [visible, setVisible] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedMotion = useRef(prefersReducedMotion());

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const handleStatus = useCallback(
    (connected: boolean) => {
      if (connected) {
        // Reconnect: hide immediately, no debounce.
        clearShowTimer();
        setVisible(false);
      } else {
        // Disconnect: debounce before showing.
        clearShowTimer();
        showTimerRef.current = setTimeout(() => {
          setVisible(true);
        }, SHOW_DEBOUNCE_MS);
      }
    },
    [clearShowTimer]
  );

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let listenerHandle: { remove: () => void } | undefined;

    (async () => {
      const status = await Network.getStatus();
      if (cancelled) return;
      handleStatus(status.connected);

      const listener = await Network.addListener("networkStatusChange", (event) => {
        if (cancelled) return;
        handleStatus(event.connected);
      });
      listenerHandle = listener;
    })();

    return () => {
      cancelled = true;
      clearShowTimer();
      listenerHandle?.remove();
    };
  }, [handleStatus, clearShowTimer]);

  if (!visible) return null;

  const transitionClass = reducedMotion.current
    ? ""
    : "transition-opacity duration-300 ease-out";

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#FBEAE0] px-6 text-center ${transitionClass}`}
      aria-live="assertive"
      role="alert"
    >
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#F44321]/10">
        <WifiOff className="h-9 w-9 text-[#F44321]" />
      </div>

      <h2 className="mt-6 font-display text-2xl font-bold text-[#2A1810]">
        You're offline
      </h2>
      <p className="mt-2 max-w-xs text-sm font-medium text-[#8A6050]">
        Check your connection and try again.
      </p>

      <button
        type="button"
        onClick={async () => {
          const status = await Network.getStatus();
          handleStatus(status.connected);
        }}
        className="mt-8 w-full max-w-xs rounded-xl bg-[#F44321] py-3 text-sm font-extrabold text-white shadow-[0_12px_24px_-12px_rgba(244,67,33,0.7)] active:scale-[0.98]"
      >
        Try again
      </button>
    </div>
  );
}
